import React from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  Chip,
  Box,
  styled,
} from '@mui/material';
import type { SelectProps as MuiSelectProps, MenuItemProps } from '@mui/material';
import { ExpandMore, Check } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Option interface
export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

// Custom select variants
export type SelectVariant = 'outlined' | 'filled' | 'standard';
export type SelectSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomSelectProps extends Omit<MuiSelectProps, 'variant' | 'size'> {
  variant?: SelectVariant;
  size?: SelectSize;
  options: SelectOption[];
  placeholder?: string;
  description?: string;
  clearable?: boolean;
  searchable?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
}

// Styled components
const StyledFormControl = styled(FormControl)<{ size?: SelectSize; error?: boolean }>(({
  theme,
  size,
  error,
}) => {
  const sizeConfig = {
    small: {
      '& .MuiInputBase-root': {
        height: { xs: 40, sm: 36 },
        fontSize: { xs: '0.875rem', sm: '0.75rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '0.875rem', sm: '0.75rem' },
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        height: { xs: 48, sm: 44 },
        fontSize: { xs: '1rem', sm: '0.875rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '1rem', sm: '0.875rem' },
      },
    },
    large: {
      '& .MuiInputBase-root': {
        height: { xs: 56, sm: 52 },
        fontSize: { xs: '1.125rem', sm: '1rem' },
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '1.125rem', sm: '1rem' },
      },
    },
  };

  return {
    ...sizeConfig[size || 'medium'],
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1),
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: error ? theme.palette.error.main : theme.palette.grey[300],
        borderWidth: 1.5,
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 0 0 2px ${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2
        )}`,
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    '& .MuiSelect-icon': {
      color: theme.palette.grey[600],
      fontSize: '1.25rem',
    },
  };
});

const StyledMenuItem = styled(MenuItem)<MenuItemProps>(({ theme }) => ({
  minHeight: { xs: 48, sm: 40 },
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: { xs: '1rem', sm: '0.875rem' },
  padding: { xs: theme.spacing(1.5, 2), sm: theme.spacing(1, 1.5) },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
}));

const MultiSelectChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
}));

export const Select: React.FC<CustomSelectProps> = ({
  variant = 'outlined',
  size = 'medium',
  options,
  placeholder,
  description,
  error = false,
  helperText,
  label,
  multiple = false,
  value,
  onChange,
  disabled = false,
  ...props
}) => {
  const displayHelperText = error ? helperText : description || helperText;

  const renderValue = (selected: unknown) => {
    if (multiple && Array.isArray(selected)) {
      if (selected.length === 0) {
        return <span style={{ color: '#999' }}>{placeholder}</span>;
      }
      return (
        <MultiSelectChips>
          {selected.map(val => {
            const option = options.find(opt => opt.value === val);
            return (
              <Chip
                key={val}
                label={option?.label || val}
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            );
          })}
        </MultiSelectChips>
      );
    }

    if (!value || value === '') {
      return <span style={{ color: '#999' }}>{placeholder}</span>;
    }

    const option = options.find(opt => opt.value === value);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {option?.icon && option.icon}
        <span>{option?.label || String(value)}</span>
      </Box>
    );
  };

  return (
    <StyledFormControl
      fullWidth
      variant={variant}
      size={size as 'small' | 'medium' | undefined}
      error={error}
      disabled={disabled}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <MuiSelect
        {...props}
        value={value}
        onChange={onChange}
        multiple={multiple}
        renderValue={renderValue}
        displayEmpty={!!placeholder}
        IconComponent={ExpandMore}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              maxHeight: { xs: '60vh', sm: '50vh' },
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: { xs: 0.5, sm: 1 },
                my: { xs: 0.25, sm: 0.5 },
              },
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {options.map(option => (
          <StyledMenuItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.icon && option.icon}
            {option.label}
            {multiple && Array.isArray(value) && value.includes(option.value) && (
              <Check fontSize="small" sx={{ ml: 'auto', color: 'primary.main' }} />
            )}
          </StyledMenuItem>
        ))}
      </MuiSelect>
      {displayHelperText && <FormHelperText error={error}>{displayHelperText}</FormHelperText>}
    </StyledFormControl>
  );
};

export default Select;
